import Navbar from '../component/Navbar';
import Footer from '../component/Footer';

const pages = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'May 4, 2026',
    intro:
      'AI Sanctuary respects your privacy. This policy explains how we collect, use, and protect information when you use our website, courses, forms, and learner dashboards.',
    sections: [
      {
        title: 'Information We Collect',
        body: [
          'Account details such as name, email address, role, login method, and password-protected authentication data.',
          'Course activity such as enrollments, learning progress, certificates, submitted forms, and instructor course content.',
          'Contact information and messages you send through our website or support channels.',
          'Basic technical data such as browser, device, IP address, and usage events needed to secure and improve the platform.',
        ],
      },
      {
        title: 'How We Use Information',
        body: [
          'To create accounts, provide access to courses, track progress, issue certificates, and manage instructor tools.',
          'To respond to contact requests, password resets, support messages, and important account notices.',
          'To improve platform reliability, security, course quality, and user experience.',
        ],
      },
      {
        title: 'Sharing and Security',
        body: [
          'We do not sell personal information. We only share data with service providers needed to operate the platform, such as hosting, database, authentication, email, and analytics services.',
          'We use reasonable safeguards to protect account and course data, but no online service can guarantee absolute security.',
        ],
      },
      {
        title: 'Your Choices',
        body: [
          'You can update basic account details from your settings page.',
          'You may contact us to request account assistance, data correction, or deletion where applicable.',
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of Services',
    updated: 'May 4, 2026',
    intro:
      'These terms explain the rules for using AI Sanctuary courses, dashboards, content, and related services.',
    sections: [
      {
        title: 'Use of the Platform',
        body: [
          'You agree to provide accurate account information and keep your login credentials secure.',
          'You may use course materials for personal learning only unless AI Sanctuary gives written permission for another use.',
          'You must not misuse the platform, attempt unauthorized access, upload harmful content, or disrupt services.',
        ],
      },
      {
        title: 'Courses and Content',
        body: [
          'Course availability, curriculum, instructor details, schedules, and certificates may change as the program improves.',
          'All website, course, video, text, design, and training content belongs to AI Sanctuary or its content providers.',
        ],
      },
      {
        title: 'Accounts',
        body: [
          'We may suspend or restrict accounts that violate these terms, misuse course content, or create security risk.',
          'Students and instructors are responsible for activity performed through their accounts.',
        ],
      },
      {
        title: 'Service Changes',
        body: [
          'We may update features, dashboards, policies, pricing, or course access rules when needed.',
          'Continued use of the platform after changes means you accept the updated terms.',
        ],
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    updated: 'May 4, 2026',
    intro:
      'This policy explains how refund requests are handled for paid AI Sanctuary courses, programs, and subscriptions.',
    sections: [
      {
        title: 'Refund Requests',
        body: [
          'Refund requests should be submitted through our contact page or official support channels with your registered email and payment details.',
          'Eligibility depends on the course, program type, attendance or access status, and the timing of the request.',
        ],
      },
      {
        title: 'Non-Refundable Cases',
        body: [
          'Completed courses, issued certificates, consumed digital content, missed live sessions, or misuse of access may not be eligible for refund.',
          'Third-party payment charges or bank fees may be excluded where applicable.',
        ],
      },
      {
        title: 'Processing',
        body: [
          'Approved refunds are processed through the original payment method where possible.',
          'Processing time can vary depending on the payment provider or bank.',
        ],
      },
    ],
  },
};

export default function LegalPage({ type }) {
  const page = pages[type] || pages.privacy;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="px-4 sm:px-8 lg:px-[80px] py-14 lg:py-20">
        <div className="mx-auto max-w-[960px]">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#285ADE]">
            AI Sanctuary
          </p>
          <h1 className="mb-4 text-[36px] font-bold leading-tight text-[#101828] sm:text-[48px]">
            {page.title}
          </h1>
          <p className="mb-8 text-sm text-[#667085]">Last updated: {page.updated}</p>
          <p className="mb-10 max-w-[820px] text-[18px] leading-8 text-[#475467]">
            {page.intro}
          </p>

          <div className="space-y-8">
            {page.sections.map((section) => (
              <section key={section.title}>
                <h2 className="mb-3 text-[24px] font-semibold text-[#101828]">
                  {section.title}
                </h2>
                <ul className="space-y-3 pl-5 text-[16px] leading-7 text-[#475467]">
                  {section.body.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-[8px] bg-[#F2F6FF] px-5 py-4 text-[15px] leading-7 text-[#344054]">
            For questions about this page, contact us at{' '}
            <a className="font-semibold text-[#285ADE]" href="mailto:learn@aisanctuary.org">
              learn@aisanctuary.org
            </a>
            .
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
